
import React from 'react';
import { ArrowLeft, Book, Bookmark, GraduationCap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen bg-background px-4 py-8 md:py-12">
      <div className="max-w-4xl mx-auto">
        <Link to="/">
          <Button variant="ghost" className="mb-8 -ml-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Quiz
          </Button>
        </Link>

        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-3">
            About Al-Ajrumiyyah
          </h1>
          <p className="arabic-subtitle text-center mb-6">
            المُقَدِّمَةُ الآجُرُّومِيَّةُ في مَبَادِئِ عِلْمِ العَرَبِيَّةِ
          </p>
          <div className="max-w-prose mx-auto">
            <p className="text-lg mb-4">
              The Al-Ajrumiyyah (الْآجُرُّومِيَّة) is a renowned classical Arabic grammar textbook written in the 13th century by the Moroccan scholar Muhammad ibn Da'ud as-Sanhaji, also known as Ibn Ajarrum.
            </p>
            <p className="text-muted-foreground">
              It is titled "Al-Muqaddimah al-Ajurrumiyyah fi Mabadi' Ilm al-Arabiyyah," which translates to "The Introduction to the Foundations of Arabic Language."
            </p>
          </div>
        </header>

        <section className="mb-12">
          <h2 className="text-2xl font-serif font-bold mb-6">Key Features of Al-Ajrumiyyah</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Book className="h-5 w-5 text-primary" />
                  <CardTitle>Concise Structure</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Al-Ajrumiyyah is renowned for its brevity and systematic approach to teaching Arabic grammar fundamentals, making it accessible for beginners.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  <CardTitle>Educational Legacy</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  For over seven centuries, this text has been a standard curriculum component in traditional Islamic educational institutions across the Arab world.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Bookmark className="h-5 w-5 text-primary" />
                  <CardTitle>Memorization Tradition</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Students typically memorize the entire text, which serves as a foundation for more advanced grammatical studies in Arabic.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-serif font-bold mb-6">Topics Covered</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium mb-2">Parts of Speech</h3>
              <p className="text-sm text-muted-foreground">Nouns, verbs, and particles</p>
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium mb-2">Declension</h3>
              <p className="text-sm text-muted-foreground">Case endings and grammatical states</p>
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium mb-2">Sentence Structure</h3>
              <p className="text-sm text-muted-foreground">Nominal and verbal sentences</p>
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium mb-2">Verb Conjugation</h3>
              <p className="text-sm text-muted-foreground">Regular and irregular verbs</p>
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium mb-2">Noun Declension</h3>
              <p className="text-sm text-muted-foreground">Cases, gender, and number</p>
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium mb-2">Particles</h3>
              <p className="text-sm text-muted-foreground">Prepositions and their functions</p>
            </div>
          </div>
        </section>

        <section>
          <div className="bg-accent p-6 rounded-xl">
            <h2 className="text-2xl font-serif font-bold mb-4">About This Quiz</h2>
            <p className="mb-4">
              This quiz application is designed to help students test their knowledge of Arabic grammar concepts from Al-Ajrumiyyah. The questions cover various topics from the text and are designed to reinforce understanding of key grammatical principles.
            </p>
            <p className="text-muted-foreground">
              Practice regularly to improve your mastery of classical Arabic grammar and build a solid foundation for advanced studies.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
